import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import SendIcon from "../../assets/SendIcon";
import RefreshIcon from "../../assets/RefreshIcon";
import CheckIcon from "../../assets/CheckIcon";
import CopyIcon from "../../assets/CopyIcon";
import ChevronDownIcon from "../../assets/ChevronDownIcon";
import {
  ChatWindowContainer,
  ChatHeader,
  ChatBody,
  ChatMessageRow,
  ChatMessage,
  ChatFooter,
  ChatInput,
  sendButtonStyle,
  TypingDotsContainer,
  TypingDot,
  HeaderTitle,
  HeaderButtonsContainer,
  IconButton,
  CloseButton,
  FooterInputContainer,
  InputWrapper,
  CharLimitWarning,
  CharCounter,
  ScrollToBottomButton,
  ChatBodyWrapper,
  CopyResponseButton,
  BotMessageContainer,
  ResizeHandle,
  HeaderTitleGroup,
  HeaderBotBadge,
  WelcomeState,
  WelcomeGreeting,
  WelcomeSubtitle,
  TryAskingLabel,
  SuggestionCard,
  SuggestionIcon,
  SuggestionText,
  SuggestionChevron,
  FooterHint,
} from "./style";
import mlopsAgentSseService from "../../services/mlopsAgentSseService";
import { RootState } from "../../api-store/store";
import {
  initializeSession,
  addMessage,
  appendToMessage,
  resetChat,
  cleanupOldSessions,
  ChatMessage as ChatMessageType,
} from "./ducks/slice";
import MarkdownRenderer from "./MarkdownRenderer";

const QUERY_MAX_LENGTH = 4000;
const QUERY_WARNING_THRESHOLD = 3500;
const SCROLL_THRESHOLD = 80;
const DEFAULT_WIDTH = 700;
const DEFAULT_HEIGHT = 620;
const WINDOW_SIZE_STORAGE_KEY = "tdsp-agent-window-size";

const SUGGESTED_QUESTIONS = [
  "What is TDSP and how do I get started with MLOps workflows?",
  "How do I create and deploy an ML pipeline?",
  "How do I onboard a new user or set up a POD?",
];

const loadWindowSize = (): { width: number; height: number } => {
  try {
    const stored = localStorage.getItem(WINDOW_SIZE_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (typeof parsed.width === "number" && typeof parsed.height === "number") {
        return {
          width: Math.max(380, Math.min(window.innerWidth * 0.95, parsed.width)),
          height: Math.max(400, Math.min(window.innerHeight - 120, parsed.height)),
        };
      }
    }
  } catch {
    // ignore parse errors
  }
  return { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
};

/**
 * Strip markdown syntax to get plain text for clipboard copy.
 */
const stripMarkdown = (md: string): string => {
  return md
    .replaceAll(/```[\s\S]*?```/g, (match) => {
      // Keep code content, remove fences
      const lines = match.split('\n');
      return lines.slice(1, -1).join('\n');
    })
    .replaceAll(/`([^`]+)`/g, '$1')
    .replaceAll(/\*\*([^*]+)\*\*/g, '$1')
    .replaceAll(/\*([^*]+)\*/g, '$1')
    .replaceAll(/~~([^~]+)~~/g, '$1')
    .replaceAll(/^#{1,6}\s+/gm, '')
    .replaceAll(/^\s*[-*+]\s+/gm, '• ')
    .replaceAll(/^\s*\d+\.\s+/gm, '')
    .replaceAll(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replaceAll(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    .replaceAll(/^>\s+/gm, '')
    .replaceAll(/\n{3,}/g, '\n\n')
    .trim();
};


const CopyResponseBtn: React.FC<{
  text: string;
  idx: number;
  copiedIdx: number | null;
  onCopy: (text: string, idx: number) => void;
}> = ({ text, idx, copiedIdx, onCopy }) => (
  <CopyResponseButton
    onClick={() => onCopy(text, idx)}
    aria-label="Copy response"
    title={copiedIdx === idx ? "Copied!" : "Copy response"}
  >
    {copiedIdx === idx ? <CheckIcon /> : <CopyIcon />}
  </CopyResponseButton>
);

const ChatWindow: React.FC<{ onClose?: () => void; docked?: boolean; onExpand?: () => void }> = ({ onClose, docked = false, onExpand }) => {
  const dispatch = useDispatch();
  
  // Get state from Redux store
  const currentSessionId = useSelector(
    (state: RootState) => state.mlopsAgent.currentSessionId
  );
  const chatSessions = useSelector(
    (state: RootState) => state.mlopsAgent.chatSessions
  );
  
  // Get messages for current session
  const messages = useMemo(
    () => currentSessionId && chatSessions[currentSessionId]
      ? chatSessions[currentSessionId].messages
      : [],
    [currentSessionId, chatSessions]
  );

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [sseReady, setSseReady] = useState(false);
  const streamingIdxRef = useRef<number | null>(null);

  // Smart auto-scroll state
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);
  const isAutoScrollingRef = useRef(false);

  // Resize state - persisted in localStorage
  const [windowSize, setWindowSize] = useState(loadWindowSize);
  const isResizingRef = useRef(false);
  const resizeStartRef = useRef({ x: 0, y: 0, width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });

  // Copy state per message index
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  // Cleanup old sessions and initialize on mount
  useEffect(() => {
    dispatch(cleanupOldSessions()); // Delete sessions older than 1 day
    dispatch(initializeSession());
  }, [dispatch]);

  // Auto-focus input when chat window opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleMessage = (chunk: string) => {
      const idx = streamingIdxRef.current;
      if (idx !== null) {
        dispatch(appendToMessage({ index: idx, chunk }));
      }
    };

    const handleConnection = (ready: boolean) => {
      setSseReady(ready);
    };

    const handleStreamEnd = () => {
      setLoading(false);
      streamingIdxRef.current = null;
    };

    const handleError = (error: string) => {
      console.error("[ChatWindow] Agent error:", error);
      const errorMessage = "Something went wrong. Please try again.";
      const idx = streamingIdxRef.current;
      if (idx === null) {
        dispatch(addMessage({ from: "bot", text: errorMessage }));
      } else {
        dispatch(appendToMessage({ index: idx, chunk: errorMessage }));
        streamingIdxRef.current = null;
      }
      setLoading(false);
    };

    // Initialize SSE service with callbacks
    mlopsAgentSseService.connect(
      handleMessage,
      handleConnection,
      handleStreamEnd,
      undefined, // userId
      currentSessionId || undefined, // sessionId
      handleError // onError callback
    );

    return () => {
      mlopsAgentSseService.disconnect();
    };
  }, [currentSessionId, dispatch]);

  // --- Smart Auto-Scroll ---
  const isNearBottom = useCallback((): boolean => {
    const el = chatRef.current;
    if (!el) { return true; }
    return el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD;
  }, []);

  // Detect user scroll intent
  const handleChatScroll = useCallback(() => {
    if (isAutoScrollingRef.current) { return; } // Ignore programmatic scrolls
    setUserHasScrolledUp(!isNearBottom());
  }, [isNearBottom]);

  // Auto-scroll only when user hasn't scrolled up
  useEffect(() => {
    if (!userHasScrolledUp && chatRef.current) {
      isAutoScrollingRef.current = true;
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
      // Reset flag after scroll event fires
      requestAnimationFrame(() => {
        isAutoScrollingRef.current = false;
      });
    }
  }, [messages, userHasScrolledUp]);

  const scrollToBottom = useCallback(() => {
    if (chatRef.current) {
      isAutoScrollingRef.current = true;
      chatRef.current.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
      setUserHasScrolledUp(false);
      setTimeout(() => {
        isAutoScrollingRef.current = false;
      }, 500);
    }
  }, []);

  // --- Resize ---
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: windowSize.width,
      height: windowSize.height,
    };

    const handleResizeMove = (moveEvent: MouseEvent) => {
      if (!isResizingRef.current) { return; }
      const dx = resizeStartRef.current.x - moveEvent.clientX;
      const dy = resizeStartRef.current.y - moveEvent.clientY;
      const newWidth = Math.max(380, Math.min(window.innerWidth * 0.95, resizeStartRef.current.width + dx));
      const newHeight = Math.max(400, Math.min(window.innerHeight - 120, resizeStartRef.current.height + dy));
      setWindowSize({ width: newWidth, height: newHeight });
    };

    const handleResizeEnd = () => {
      isResizingRef.current = false;
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      // Persist final size
      setWindowSize((current) => {
        try { localStorage.setItem(WINDOW_SIZE_STORAGE_KEY, JSON.stringify(current)); } catch { /* ignore */ }
        return current;
      });
    };

    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  }, [windowSize]);

  // --- Copy Response ---
  const handleCopyResponse = useCallback((text: string, idx: number) => {
    const plainText = stripMarkdown(text);
    navigator.clipboard.writeText(plainText).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  }, []);

  const handleSend = useCallback(() => {
    if (!input.trim() || loading || !sseReady) { return; }
    
    // Validate query length before sending
    if (input.length > QUERY_MAX_LENGTH) {
      return;
    }
    
    const userMsg = input.trim();
    
    // Add user message to store
    dispatch(addMessage({ from: "user", text: userMsg }));
    
    setInput("");
    setLoading(true);
    
    // Re-enable auto-scroll when user sends a new message
    setUserHasScrolledUp(false);
    
    // Add empty bot message for streaming
    dispatch(addMessage({ from: "bot", text: "" }));
    
    // Set streaming index to the new bot message
    streamingIdxRef.current = messages.length + 1; // +1 because we just added user msg
    
    // Send message via SSE
    mlopsAgentSseService.sendMessage(userMsg);
  }, [input, loading, sseReady, messages.length, dispatch]);

  const handleSuggestionClick = useCallback((question: string) => {
    if (loading || !sseReady) { return; }
    dispatch(addMessage({ from: "user", text: question }));
    setInput("");
    setLoading(true);
    setUserHasScrolledUp(false);
    dispatch(addMessage({ from: "bot", text: "" }));
    streamingIdxRef.current = messages.length + 1;
    mlopsAgentSseService.sendMessage(question);
  }, [loading, sseReady, messages.length, dispatch]);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSend();
    }
  };

  const handleRefresh = useCallback(() => {
    // Clear session before resetting
    mlopsAgentSseService.clearSession();
    
    // Reset chat to new session
    dispatch(resetChat());
    setLoading(false);
    streamingIdxRef.current = null;
    setUserHasScrolledUp(false);
    
    // Disconnect SSE service
    mlopsAgentSseService.disconnect();
  }, [dispatch]);

  return (
    <ChatWindowContainer $width={windowSize.width} $height={windowSize.height} $docked={docked}>
      {!docked && <ResizeHandle onMouseDown={handleResizeStart} title="Drag to resize" />}
      <ChatHeader>
        <HeaderTitleGroup>
          <HeaderBotBadge>🤖</HeaderBotBadge>
          <HeaderTitle>TDSP Agent</HeaderTitle>
        </HeaderTitleGroup>
        <HeaderButtonsContainer>
          <IconButton
            onClick={handleRefresh}
            aria-label="Reset chat"
            title="Start new conversation"
          >
            <RefreshIcon width={16} height={16} />
          </IconButton>
          {docked && onExpand && (
            <IconButton
              onClick={onExpand}
              aria-label="Expand chat window"
              title="Expand to full window"
            >
              <OpenInFullRoundedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          )}
          {!docked && onClose && (
            <CloseButton
              onClick={onClose}
              aria-label="Close chat window"
            >
              ×
            </CloseButton>
          )}
        </HeaderButtonsContainer>
      </ChatHeader>
      <ChatBodyWrapper>
        <ChatBody ref={chatRef} onScroll={handleChatScroll}>
          {!messages.some((m) => m.from === "user") ? (
            <WelcomeState>
              <WelcomeGreeting>Hi There! 👋</WelcomeGreeting>
              <WelcomeSubtitle>How can I help you today?</WelcomeSubtitle>
              <TryAskingLabel>✨ Try Asking...</TryAskingLabel>
              {SUGGESTED_QUESTIONS.map((question) => (
                <SuggestionCard
                  key={question}
                  onClick={() => handleSuggestionClick(question)}
                  disabled={loading || !sseReady}
                  type="button"
                >
                  <SuggestionIcon>?</SuggestionIcon>
                  <SuggestionText>{question}</SuggestionText>
                  <SuggestionChevron>›</SuggestionChevron>
                </SuggestionCard>
              ))}
            </WelcomeState>
          ) : (
            messages.map((msg: ChatMessageType, idx: number) => (
            <ChatMessageRow from={msg.from} key={idx}>
              {(() => {
                if (msg.from === "bot" && msg.text === "" && loading) {
                  return (
                    <TypingDotsContainer>
                      <TypingDot />
                      <TypingDot />
                      <TypingDot />
                    </TypingDotsContainer>
                  );
                }
                if (msg.from === "bot") {
                  return (
                    <BotMessageContainer>
                      <ChatMessage from={msg.from}>
                        <MarkdownRenderer content={msg.text} />
                      </ChatMessage>
                      {msg.text && !(loading && streamingIdxRef.current === idx) && (
                        <CopyResponseBtn
                          text={msg.text}
                          idx={idx}
                          copiedIdx={copiedIdx}
                          onCopy={handleCopyResponse}
                        />
                      )}
                    </BotMessageContainer>
                  );
                }
                return (
                  <ChatMessage from={msg.from}>
                    {msg.text}
                  </ChatMessage>
                );
              })()}
            </ChatMessageRow>
            ))
          )}
        </ChatBody>
        {userHasScrolledUp && (
          <ScrollToBottomButton
            onClick={scrollToBottom}
            aria-label="Scroll to bottom"
            title="Scroll to latest message"
          >
            <ChevronDownIcon />
          </ScrollToBottomButton>
        )}
      </ChatBodyWrapper>
      <ChatFooter>
        <FooterInputContainer>
          <InputWrapper>
            <ChatInput
              ref={inputRef}
              type="text"
              placeholder="Type your questions here..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleInputKeyDown}
              disabled={loading}
              maxLength={QUERY_MAX_LENGTH}
              style={{ width: '100%', paddingRight: 48, fontSize: 14, color: '#666666', height: "45px" }}
            />
            <button
              onClick={handleSend}
              aria-label="Send message"
              style={{
                ...sendButtonStyle,
                cursor: loading || !input.trim() || input.length > QUERY_MAX_LENGTH ? 'not-allowed' : 'pointer',
                opacity: loading || !input.trim() || input.length > QUERY_MAX_LENGTH ? 0.6 : 1
              }}
              disabled={loading || !input.trim() || input.length > QUERY_MAX_LENGTH}
            >
              <SendIcon width={15} height={15} />
            </button>
          </InputWrapper>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
            {input.length >= QUERY_WARNING_THRESHOLD ? (
              <CharLimitWarning>
                {input.length >= QUERY_MAX_LENGTH 
                  ? `Maximum character limit reached (${QUERY_MAX_LENGTH})` 
                  : `Approaching character limit`}
              </CharLimitWarning>
            ) : <span />}
            <CharCounter $isWarning={input.length >= QUERY_WARNING_THRESHOLD} $isError={input.length >= QUERY_MAX_LENGTH}>
              {input.length.toLocaleString()}/{QUERY_MAX_LENGTH.toLocaleString()}
            </CharCounter>
          </div>
          <FooterHint>✨ Ask anything about TDSP · Pipelines · PODs · Onboarding · Deployments</FooterHint>
        </FooterInputContainer>
      </ChatFooter>
    </ChatWindowContainer>
  );
};

export default ChatWindow;